import toast from 'react-hot-toast';

// Listener for dialog mode — set by ErrorDialog component
export type DialogType = 'error' | 'success';
type DialogListener = (messages: string[], type: DialogType) => void;
let dialogListener: DialogListener | null = null;

export function setErrorDialogListener(fn: DialogListener) {
  dialogListener = fn;
}

export function removeErrorDialogListener() {
  dialogListener = null;
}

/**
 * Extracts error messages from a Laravel API error response.
 */
function extractMessages(error: any, fallbackMessage: string): string[] {
  const data = error?.response?.data;

  if (data?.errors) {
    return Object.values(data.errors).flat() as string[];
  }
  return [data?.message || data?.error || fallbackMessage];
}

/**
 * Shows error messages from a Laravel API error response.
 *
 * @param mode - 'toast' shows each error as a toast notification (default),
 *               'dialog' shows all errors in a modal dialog
 *
 * Usage:
 *   } catch (error: any) {
 *     handleApiError(error, 'Hiba történt a mentéskor');
 *   }
 */
export function handleApiError(
  error: any,
  fallbackMessage: string,
  mode: 'toast' | 'dialog' = 'toast'
): void {
  // nginx returns 413 (plain HTML, no Laravel JSON) when the request body
  // exceeds client_max_body_size. Surface a clear message instead of a generic error.
  if (error?.response?.status === 413) {
    showError('A fájl túl nagy. Maximum 10 MB tölthető fel.', mode);
    return;
  }
  const messages = extractMessages(error, fallbackMessage);
  showError(messages, mode);
}

/**
 * Shows error message(s) as toast (default) or dialog.
 * Use for client-side validation errors and non-API errors.
 */
export function showError(
  message: string | string[],
  mode: 'toast' | 'dialog' = 'toast'
): void {
  const messages = Array.isArray(message) ? message : [message];
  if (mode === 'dialog' && dialogListener) {
    dialogListener(messages, 'error');
  } else {
    messages.forEach((msg) => toast.error(msg));
  }
}

/**
 * Shows success message(s) as toast (default) or dialog.
 */
export function showSuccess(
  message: string | string[],
  mode: 'toast' | 'dialog' = 'toast'
): void {
  const messages = Array.isArray(message) ? message : [message];
  if (mode === 'dialog' && dialogListener) {
    dialogListener(messages, 'success');
  } else {
    messages.forEach((msg) => toast.success(msg));
  }
}
